import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import {
  ArrowRight, Hotel, DollarSign, TrendingUp, Shield, Coins,
  Zap, Globe, Users, BarChart3, Layers, Eye, Lock,
  ChevronDown, Sparkles, Target, Gift, Award, Activity,
} from 'lucide-react';
import { BACKEND_URL, API } from '@/lib/backendUrl';
import SEO from '@/components/seo/SEO';

/* ─── COMPETITOR DATA ─── */
const COMPETITORS = [
  {
    name: 'GuestCentric',
    type: 'HyperCommerce SaaS',
    setupFee: '$5,000 – $15,000',
    setupFeeNum: 10000,
    annualCommission: '6% (capped $499/mo)',
    annualCapNum: 5988,
    features: ['Website builder', 'Booking engine', 'Channel manager', 'Payment gateway'],
    missing: ['3D Metaverse', 'Token economy', 'AI agents', 'Vendor ecosystem', 'Guest program', 'On-chain identity'],
    revenueBack: '0%',
    revenueBackPct: 0,
  },
  {
    name: 'Mews',
    type: 'Cloud PMS',
    setupFee: '$2,000 – $8,000',
    setupFeeNum: 5000,
    annualCommission: '$8 – $20/room/mo',
    annualCapNum: 7200,
    features: ['Cloud PMS', 'Payment automation', 'Revenue management', 'API marketplace'],
    missing: ['3D viewer', 'Token utility', 'AI booking', 'Creator marketplace', 'Vendor protocol', 'Blockchain identity'],
    revenueBack: '0%',
    revenueBackPct: 0,
  },
  {
    name: 'Cloudbeds',
    type: 'Hospitality Platform',
    setupFee: '$1,500 – $5,000',
    setupFeeNum: 3000,
    annualCommission: '$3 – $10/room/mo',
    annualCapNum: 4800,
    features: ['PMS', 'Channel manager', 'Booking engine', 'Revenue management'],
    missing: ['Metaverse presence', 'Token utility', 'AI concierge', 'Vendor showroom', 'Learn-to-Engage', 'SBT identity'],
    revenueBack: '0%',
    revenueBackPct: 0,
  },
];

const GETBITSY = {
  name: 'getbitsy.ai',
  type: 'Web3 Hospitality Protocol',
  setupFee: 'Token mint (no cash)',
  setupFeeNum: 0,
  annualCommission: '6% of bookings',
  features: [
    'Full PMS & Front Desk',
    'AI Booking Agent (Bitsy)',
    'Channel Manager (200+ OTAs)',
    'Housekeeping & Staff Mgmt',
    '3D Metaverse Hotel Viewer',
    'HERMES Token Economy',
    'Soulbound Token Identity',
    'Creator Marketplace',
    'Guest Engagement Program',
    'Cross-chain (LayerZero)',
    'Crypto Payment Ledger',
    'Revenue Comparison Dashboard',
  ],
  revenueBack: '1% → protocol participation',
  revenueBackPct: 1,
};

const OTA_COMMISSIONS = [
  { name: 'Booking.com', pct: 15, barColor: 'bg-blue-500/70' },
  { name: 'Expedia', pct: 20, barColor: 'bg-yellow-600/70' },
  { name: 'Hotels.com', pct: 22, barColor: 'bg-red-500/70' },
  { name: 'Agoda', pct: 18, barColor: 'bg-purple-500/70' },
  { name: 'Airbnb', pct: 15, barColor: 'bg-rose-500/70' },
  { name: 'TripAdvisor', pct: 15, barColor: 'bg-emerald-500/70' },
];

const DIFFERENTIATORS = [
  { icon: Coins, title: '3% Fiat + 3% HERMES', desc: 'Same 6% as competitors, but split 50/50. 3% covers operations, 3% stays on-chain — rewards, referrals, and treasury.' },
  { icon: Globe, title: 'Channel Manager Included', desc: 'One-click distribution to Booking.com, Expedia, Airbnb & 200+ OTAs. No setup, no extra cost.' },
  { icon: Sparkles, title: 'AI Booking Agent (Bitsy)', desc: 'Guests book by conversation, not forms. Voice-enabled, multilingual, 30-second booking flow.' },
  { icon: Shield, title: 'On-Chain Accountability', desc: 'Soulbound Tokens track reputation. Quality scores govern protocol standing. Bad actors get auto-slashed.' },
  { icon: Gift, title: 'Referral Loyalty', desc: 'Refer another hotel and getbitsy gives you its entire 1% HERMES revenue from their bookings. Ongoing protocol benefits for life.' },
  { icon: Layers, title: 'Full PMS Stack', desc: 'Front desk, housekeeping, staff management, pricing, analytics, crypto payments — everything in one platform.' },
];

/* ─── FORMAT ─── */
const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fmtNum = (n) => new Intl.NumberFormat('en-US').format(Math.round(n));


export default function WhyGetbitsyPage() {
  /* Cost Comparison Calculator State */
  const [rooms, setRooms] = useState(50);
  const [avgRate, setAvgRate] = useState(150);
  const [occupancy, setOccupancy] = useState(65);
  const [years, setYears] = useState(3);

  /* Live Protocol Stats */
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/public/protocol-stats`)
      .then(r => r.json())
      .then(setStats)
      .catch(() => {});
  }, []);

  const calc = useMemo(() => {
    const annualRevenue = rooms * avgRate * (occupancy / 100) * 365;
    const getbitsyCommission = annualRevenue * 0.06;
    const fiatOps = annualRevenue * 0.03;
    const protocolAllocation = annualRevenue * 0.01;
    const treasury = annualRevenue * 0.01;
    const getbitsyRevenue = annualRevenue * 0.01;
    const hermesOnChain = protocolAllocation + treasury + getbitsyRevenue;

    // OTA comparison
    const avgOtaRate = 0.18; // 18% average
    const otaCost = annualRevenue * avgOtaRate;
    const otaSavingsPerYear = otaCost - getbitsyCommission;

    const competitors = COMPETITORS.map(c => {
      const yr1 = c.setupFeeNum + Math.min(annualRevenue * 0.06, c.annualCapNum);
      const yrN = Math.min(annualRevenue * 0.06, c.annualCapNum);
      const totalCost = yr1 + yrN * (years - 1);
      return { ...c, yr1, yrN, totalCost };
    });

    const getbitsyYr1 = getbitsyCommission;
    const getbitsyTotal = getbitsyCommission * years;
    const totalRewardBack = protocolAllocation * years;

    return {
      annualRevenue,
      getbitsyCommission,
      fiatOps,
      protocolAllocation,
      treasury,
      getbitsyRevenue,
      hermesOnChain,
      otaCost,
      otaSavingsPerYear,
      otaSavingsTotal: otaSavingsPerYear * years,
      getbitsyYr1,
      getbitsyTotal,
      totalRewardBack,
      competitors,
    };
  }, [rooms, avgRate, occupancy, years]);

  return (
    <div className="lobby-theme min-h-screen" data-testid="why-getbitsy-page">
      <SEO
        title="Why getbitsy — Direct Booking Engine vs Booking.com / Mews / Cloudbeds"
        description="Compare getbitsy.ai vs traditional hotel SaaS. Interactive ROI calculator, 6% commission breakdown (5% platform + 1% protocol rewards), and competitor table. Middleman-Free Hospitality powered by the BHERMES Protocol."
        canonical="https://getbitsy.ai/why-getbitsy"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            { '@type': 'Question', name: 'How is getbitsy.ai different from Booking.com?',
              acceptedAnswer: { '@type': 'Answer', text: 'getbitsy.ai is a Direct Booking Engine — guests book hotels directly, hotels keep 94% of revenue. Booking.com takes 15–20% as an OTA middleman. getbitsy charges 6% total: 5% platform + 1% redistributed to top operators as on-chain $HERMES rewards.' } },
            { '@type': 'Question', name: 'How much do hotels save with getbitsy?',
              acceptedAnswer: { '@type': 'Answer', text: 'A 50-room hotel at $150 ADR / 65% occupancy saves approximately $200K+ per year in OTA commissions plus eliminates $30K-$80K of SaaS subscriptions (PMS, channel manager, booking engine, AI tools, revenue manager). The 1% protocol pool returns additional rewards monthly.' } },
            { '@type': 'Question', name: 'Do I need crypto knowledge to run a getbitsy hotel?',
              acceptedAnswer: { '@type': 'Answer', text: 'No. Hotels can run getbitsy.ai with Stripe-only payments. The on-chain $HERMES rewards accumulate in your operator wallet automatically; cash out, ignore, or stake at your discretion.' } },
          ],
        }}
      />
      <div className="absolute inset-0 lobby-glow pointer-events-none" />
      <div className="lobby-grain" />

      {/* ─── NAV ─── */}
      <nav className="relative z-10 border-b border-[color:var(--l-line)] bg-[color:var(--l-bg)]/80 backdrop-blur-md sticky top-0">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-[color:var(--l-copper)] flex items-center justify-center">
              <span className="l-display text-[color:var(--l-bg)] text-sm">b</span>
            </div>
            <span className="l-display text-[17px]">getbitsy</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/marketplace" className="hidden sm:inline-flex">
              <button className="l-btn-ghost h-10 px-4 text-sm">Stays</button>
            </Link>
            <Link to="/protocol" className="hidden sm:inline-flex">
              <button className="l-btn-ghost h-10 px-4 text-sm">Protocol</button>
            </Link>
            <Link to="/register">
              <button className="l-btn-copper h-10 px-5 text-sm" data-testid="why-getbitsy-cta-nav">List your hotel</button>
            </Link>
          </div>
        </div>
      </nav>

      {/* ─── HERO ─── */}
      <section className="relative z-10 overflow-hidden py-20 sm:py-28 border-b border-[color:var(--l-line)]">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-10 relative">
          <div className="max-w-3xl lobby-reveal">
            <div className="text-[13px] tracking-wide text-[color:var(--l-cream-3)] mb-4 inline-flex items-center gap-2" data-testid="hero-badge">
              <Target className="w-3.5 h-3.5 text-[color:var(--l-copper)]" /> For hotel operators
            </div>
            <h1 className="l-display text-5xl sm:text-6xl lg:text-7xl">
              Stop renting a<br />
              <span className="text-[color:var(--l-copper)]">booking engine.</span><br />
              <span className="text-[color:var(--l-cream-3)]">Own the protocol.</span>
            </h1>
            <p className="mt-8 text-lg text-[color:var(--l-cream-2)] max-w-xl leading-relaxed">
              Traditional hotel SaaS charges you thousands for a website and takes 6% of every booking.
              getbitsy takes the same 6% &mdash; but puts half of it back on-chain to grow the ecosystem your hotel now belongs to.
            </p>
            <div className="flex flex-wrap gap-3 mt-10">
              <Link to="/register">
                <button className="l-btn-copper h-12 px-7 text-sm inline-flex items-center gap-2" data-testid="hero-get-started">
                  <Hotel className="w-4 h-4" /> List your hotel
                </button>
              </Link>
              <a href="#calculator">
                <button className="l-btn-ghost h-12 px-7 text-sm inline-flex items-center gap-2" data-testid="hero-see-savings">
                  <BarChart3 className="w-4 h-4" /> Compare costs
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PROTOCOL VIEW (operator-facing terminal) ─── */}
      {stats && (
        <section className="relative z-10 py-16 sm:py-20 border-b border-[color:var(--l-line)] overflow-hidden" data-testid="live-stats-section">
          <div
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(rgba(208,154,93,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(208,154,93,0.06) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
            }}
          />
          <div className="max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-10 relative">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-8">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[color:var(--l-copper)] inline-flex items-center gap-2" data-testid="live-stats-badge">
                  <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--l-copper)] animate-pulse" /> Protocol view &mdash; live
                </div>
                <h2 className="l-display text-3xl sm:text-4xl mt-3">The numbers on the wall.</h2>
                <p className="text-[color:var(--l-cream-2)] mt-2 max-w-lg text-[15px]">
                  Real hotels. Real bookings. Real token flow. What traditional SaaS calls a &ldquo;dashboard&rdquo; we call daylight.
                </p>
              </div>
              <div className="font-mono text-[11px] text-[color:var(--l-cream-3)] text-right">
                <div>protocol.getbitsy/v1.0.8</div>
                <div className="mt-1">sampled every 60s &middot; base_sepolia</div>
              </div>
            </div>

            {/* Terminal stat grid — sharp 1px borders, mono labels, copper values */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-[color:var(--l-line)] border border-[color:var(--l-line)]">
              {[
                { label: 'Hotels',       value: stats.total_hotels,                     icon: Hotel },
                { label: 'Countries',    value: stats.countries_count,                  icon: Globe },
                { label: 'Bookings',     value: stats.total_bookings,                   icon: BarChart3 },
                { label: 'SBTs minted',  value: stats.total_sbts,                       icon: Shield },
                { label: 'Guest tokens', value: `${fmtNum(stats.total_guest_rewards)}`, icon: Gift, suffix: 'H' },
                { label: 'Creators',     value: stats.total_creators,                   icon: Users },
              ].map(({ label, value, icon: Icon, suffix }) => (
                <div
                  key={label}
                  className="bg-[color:var(--l-bg-2)] p-4 sm:p-5"
                  data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className="flex items-center justify-between">
                    <Icon className="w-3.5 h-3.5 text-[color:var(--l-copper)]" />
                    <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[color:var(--l-cream-3)]">{label}</span>
                  </div>
                  <div className="mt-3 flex items-baseline gap-1.5">
                    <span className="l-display text-2xl sm:text-[28px] tabular-nums text-[color:var(--l-cream)]">
                      {typeof value === 'number' ? fmtNum(value) : value}
                    </span>
                    {suffix && <span className="font-mono text-[11px] text-[color:var(--l-copper)]">{suffix}</span>}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 font-mono text-[10px] text-[color:var(--l-cream-3)] flex items-center justify-between">
              <span>signed · attested · on-chain</span>
              <span>&middot; no guest PII surfaced here</span>
            </div>
          </div>
        </section>
      )}

      {/* ─── THE 6% BREAKDOWN ─── */}
      <section className="py-16 sm:py-20 bg-card/30" data-testid="commission-breakdown-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-lg md:text-xl heading-font font-semibold mb-3">The 6% that works for you</h2>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto">
              Every competitor charges ~6%. The difference? Where that money goes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-start">
            {/* Traditional */}
            <Card className="border-2 border-border/60">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <h3 className="font-semibold heading-font">Traditional SaaS</h3>
                    <p className="text-xs text-muted-foreground">GuestCentric, Mews, Cloudbeds</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="h-10 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center px-4">
                    <span className="text-sm font-medium text-red-600 dark:text-red-400">6% → Platform shareholders</span>
                  </div>
                  <div className="h-10 rounded-lg bg-muted/50 flex items-center px-4">
                    <span className="text-sm text-muted-foreground">0% back to your hotel</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-4">Plus setup fees ($2K–$15K), annual licenses, and per-room charges.</p>
              </CardContent>
            </Card>

            {/* getbitsy */}
            <Card className="border-2 border-primary/40 shadow-lg shadow-primary/5">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Coins className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold heading-font">getbitsy.ai</h3>
                    <p className="text-xs text-muted-foreground">HERMES Protocol</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">3% Fiat (Operations)</p>
                  <div className="h-9 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center px-4">
                    <span className="text-sm font-medium text-purple-600 dark:text-purple-400">3% → Run tech stack (servers, Channex, team)</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium mt-3">3% HERMES Tokens (On-chain)</p>
                  <div className="h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center px-4">
                    <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">1% → Protocol allocation (distributed to top hotels)</span>
                  </div>
                  <div className="h-9 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center px-4">
                    <span className="text-sm font-medium text-amber-600 dark:text-amber-400">1% → Treasury (BTC/HERMES reserve)</span>
                  </div>
                  <div className="h-9 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center px-4">
                    <span className="text-sm font-medium text-violet-600 dark:text-violet-400">1% → getbitsy revenue (includes referral fees)</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-4">No setup fees. No per-room charges. 50% of your commission stays on-chain building the ecosystem.</p>
              </CardContent>
            </Card>
          </div>

          {/* Visual flow */}
          <div className="mt-12 text-center">
            <Card className="inline-block border-dashed border-2">
              <CardContent className="p-4 sm:p-6 flex flex-wrap items-center justify-center gap-3 text-sm">
                <span className="font-medium heading-font">Guest books</span>
                <ArrowRight className="w-4 h-4 text-muted-foreground hidden sm:block" />
                <Badge variant="secondary">6% commission</Badge>
                <ArrowRight className="w-4 h-4 text-muted-foreground hidden sm:block" />
                <span className="text-purple-600 dark:text-purple-400 font-medium">3% fiat ops</span>
                <span className="text-muted-foreground">+</span>
                <span className="text-emerald-600 dark:text-emerald-400 font-medium">1% allocation</span>
                <span className="text-muted-foreground">+</span>
                <span className="text-amber-600 dark:text-amber-400 font-medium">1% treasury</span>
                <span className="text-muted-foreground">+</span>
                <span className="text-violet-600 dark:text-violet-400 font-medium">1% getbitsy</span>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ─── OTA COMPARISON ─── */}
      <section className="py-16 sm:py-20" data-testid="ota-comparison-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-lg md:text-xl heading-font font-semibold mb-3">vs. OTA Commissions</h2>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto">
              OTAs take 15–22% of every booking. getbitsy takes 6% — and gives half back to the ecosystem.
            </p>
          </div>
          <div className="max-w-2xl mx-auto space-y-3">
            {/* getbitsy bar */}
            <div className="flex items-center gap-3">
              <span className="w-28 text-right text-sm font-semibold text-primary">getbitsy.ai</span>
              <div className="flex-1 h-8 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full flex items-center px-3 transition-all duration-700" style={{ width: '24%' }}>
                  <span className="text-[11px] font-bold text-primary-foreground">6%</span>
                </div>
              </div>
            </div>
            {OTA_COMMISSIONS.map(ota => (
              <div key={ota.name} className="flex items-center gap-3">
                <span className="w-28 text-right text-sm text-muted-foreground">{ota.name}</span>
                <div className="flex-1 h-8 bg-muted rounded-full overflow-hidden">
                  <div className={`h-full ${ota.barColor} rounded-full flex items-center px-3 transition-all duration-700`} style={{ width: `${(ota.pct / 25) * 100}%` }}>
                    <span className="text-[11px] font-bold text-white">{ota.pct}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Card className="inline-block bg-emerald-500/5 border border-emerald-500/20">
              <CardContent className="p-4 flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                <p className="text-sm">
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">Hotels keep 94%</span>
                  <span className="text-muted-foreground"> of revenue with getbitsy vs </span>
                  <span className="font-semibold text-red-500">78–85%</span>
                  <span className="text-muted-foreground"> with OTAs</span>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ─── DIFFERENTIATORS ─── */}
      <section className="py-16 sm:py-20" data-testid="differentiators-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-lg md:text-xl heading-font font-semibold mb-3">Everything they don't have</h2>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto">
              Traditional hotel SaaS gives you a website and booking engine. getbitsy gives you an entire economy.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {DIFFERENTIATORS.map(({ icon: Icon, title, desc }, i) => (
              <Card key={i} className="room-card-hover" data-testid={`diff-card-${i}`}>
                <CardContent className="p-5">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold heading-font text-sm mb-1.5">{title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ─── COMPETITOR COMPARISON TABLE ─── */}
      <section className="py-16 sm:py-20 bg-card/30" data-testid="comparison-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-lg md:text-xl heading-font font-semibold mb-3">Side-by-side comparison</h2>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto">
              See exactly what you get — and what you don't — with traditional hotel tech.
            </p>
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm" data-testid="comparison-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground"></th>
                  {COMPETITORS.map(c => (
                    <th key={c.name} className="text-left py-3 px-4 font-medium text-muted-foreground">{c.name}</th>
                  ))}
                  <th className="text-left py-3 px-4 font-semibold text-primary heading-font">getbitsy.ai</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border/50">
                  <td className="py-3 px-4 font-medium">Type</td>
                  {COMPETITORS.map(c => <td key={c.name} className="py-3 px-4 text-muted-foreground">{c.type}</td>)}
                  <td className="py-3 px-4 font-medium text-primary">{GETBITSY.type}</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-3 px-4 font-medium">Setup Cost</td>
                  {COMPETITORS.map(c => <td key={c.name} className="py-3 px-4 text-red-500">{c.setupFee}</td>)}
                  <td className="py-3 px-4 text-emerald-600 font-medium">{GETBITSY.setupFee}</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-3 px-4 font-medium">Commission</td>
                  {COMPETITORS.map(c => <td key={c.name} className="py-3 px-4 text-muted-foreground">{c.annualCommission}</td>)}
                  <td className="py-3 px-4">{GETBITSY.annualCommission}</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-3 px-4 font-medium">Protocol Participation</td>
                  {COMPETITORS.map(c => <td key={c.name} className="py-3 px-4 text-muted-foreground">{c.revenueBack}</td>)}
                  <td className="py-3 px-4 text-amber-600 dark:text-amber-400 font-medium">{GETBITSY.revenueBack}</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-3 px-4 font-medium">3D Metaverse</td>
                  {COMPETITORS.map(c => <td key={c.name} className="py-3 px-4 text-muted-foreground">-</td>)}
                  <td className="py-3 px-4 text-emerald-600">Included</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-3 px-4 font-medium">AI Agents</td>
                  {COMPETITORS.map(c => <td key={c.name} className="py-3 px-4 text-muted-foreground">-</td>)}
                  <td className="py-3 px-4 text-emerald-600">Included</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-3 px-4 font-medium">Token Economy</td>
                  {COMPETITORS.map(c => <td key={c.name} className="py-3 px-4 text-muted-foreground">-</td>)}
                  <td className="py-3 px-4 text-emerald-600">HERMES Token</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-3 px-4 font-medium">Guest Program</td>
                  {COMPETITORS.map(c => <td key={c.name} className="py-3 px-4 text-muted-foreground">Basic loyalty</td>)}
                  <td className="py-3 px-4 text-emerald-600">Token-Based Engagement</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium">Vendor Ecosystem</td>
                  {COMPETITORS.map(c => <td key={c.name} className="py-3 px-4 text-muted-foreground">-</td>)}
                  <td className="py-3 px-4 text-emerald-600">Full Protocol</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-4">
            {[...COMPETITORS, { ...GETBITSY, isGetbitsy: true }].map((c) => (
              <Card key={c.name} className={c.isGetbitsy ? 'border-2 border-primary/40' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className={`font-semibold heading-font text-sm ${c.isGetbitsy ? 'text-primary' : ''}`}>{c.name}</h3>
                    <Badge variant={c.isGetbitsy ? 'default' : 'secondary'} className="text-[10px]">{c.type}</Badge>
                  </div>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between"><span className="text-muted-foreground">Setup</span><span className={c.isGetbitsy ? 'text-emerald-600 font-medium' : 'text-red-500'}>{c.setupFee}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Commission</span><span>{c.annualCommission}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Protocol Participation</span><span className={c.isGetbitsy ? 'text-amber-600 font-medium' : ''}>{c.revenueBack}</span></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ─── COST COMPARISON CALCULATOR ─── */}
      <section className="py-16 sm:py-20" id="calculator" data-testid="cost-comparison-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-lg md:text-xl heading-font font-semibold mb-3">Cost Comparison Calculator</h2>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto">
              Enter your hotel's numbers. See exactly what you pay — and where it goes — with getbitsy vs legacy platforms.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* Inputs */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardContent className="p-6 space-y-6">
                  <div>
                    <label className="text-sm font-medium heading-font flex items-center justify-between mb-3">
                      <span>Rooms</span>
                      <span className="text-primary tabular-nums">{rooms}</span>
                    </label>
                    <Slider
                      value={[rooms]}
                      onValueChange={(v) => setRooms(v[0])}
                      min={10}
                      max={500}
                      step={5}
                      data-testid="slider-rooms"
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground mt-1"><span>10</span><span>500</span></div>
                  </div>

                  <div>
                    <label className="text-sm font-medium heading-font flex items-center justify-between mb-3">
                      <span>Avg. Nightly Rate</span>
                      <span className="text-primary tabular-nums">{fmt(avgRate)}</span>
                    </label>
                    <Slider
                      value={[avgRate]}
                      onValueChange={(v) => setAvgRate(v[0])}
                      min={50}
                      max={800}
                      step={10}
                      data-testid="slider-rate"
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground mt-1"><span>$50</span><span>$800</span></div>
                  </div>

                  <div>
                    <label className="text-sm font-medium heading-font flex items-center justify-between mb-3">
                      <span>Occupancy</span>
                      <span className="text-primary tabular-nums">{occupancy}%</span>
                    </label>
                    <Slider
                      value={[occupancy]}
                      onValueChange={(v) => setOccupancy(v[0])}
                      min={20}
                      max={95}
                      step={1}
                      data-testid="slider-occupancy"
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground mt-1"><span>20%</span><span>95%</span></div>
                  </div>

                  <div>
                    <label className="text-sm font-medium heading-font flex items-center justify-between mb-3">
                      <span>Projection Period</span>
                      <span className="text-primary tabular-nums">{years} {years === 1 ? 'year' : 'years'}</span>
                    </label>
                    <Slider
                      value={[years]}
                      onValueChange={(v) => setYears(v[0])}
                      min={1}
                      max={5}
                      step={1}
                      data-testid="slider-years"
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground mt-1"><span>1yr</span><span>5yr</span></div>
                  </div>

                  <Separator />
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Est. Annual Revenue</p>
                    <p className="text-2xl font-semibold heading-font tabular-nums text-foreground mt-1">{fmt(calc.annualRevenue)}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Results */}
            <div className="lg:col-span-3 space-y-4">
              {/* OTA Savings Hero */}
              <Card className="border-2 border-emerald-500/40 shadow-lg shadow-emerald-500/5 bg-emerald-500/[0.02]" data-testid="result-ota-savings">
                <CardContent className="p-6 text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">vs. OTAs (avg 18%)</p>
                  <p className="text-3xl sm:text-4xl font-bold heading-font tabular-nums text-emerald-500">
                    {fmt(calc.otaSavingsTotal)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    saved over {years} {years === 1 ? 'year' : 'years'} by using getbitsy instead of Booking.com/Expedia
                  </p>
                  <div className="flex justify-center gap-6 mt-4 text-xs">
                    <div>
                      <p className="text-red-500 font-semibold">{fmt(calc.otaCost)}/yr</p>
                      <p className="text-muted-foreground">OTA cost (18%)</p>
                    </div>
                    <div>
                      <p className="text-primary font-semibold">{fmt(calc.getbitsyCommission)}/yr</p>
                      <p className="text-muted-foreground">getbitsy (6%)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* getbitsy fee split */}
              <Card className="border-2 border-primary/40 shadow-lg shadow-primary/5" data-testid="result-getbitsy">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                      <span className="text-primary-foreground font-bold text-xs">B</span>
                    </div>
                    <div>
                      <h3 className="font-semibold heading-font text-sm">getbitsy.ai — Your 6% Breakdown</h3>
                      <p className="text-[10px] text-muted-foreground">Per year at {fmt(calc.annualRevenue)} revenue</p>
                    </div>
                  </div>
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between bg-purple-500/5 border border-purple-500/10 rounded-lg px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-purple-500" />
                        <span className="text-sm">Operations (3% fiat)</span>
                      </div>
                      <span className="font-semibold mono text-sm">{fmt(calc.fiatOps)}</span>
                    </div>
                    <div className="flex items-center justify-between bg-emerald-500/5 border border-emerald-500/10 rounded-lg px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-emerald-500" />
                        <span className="text-sm">Protocol Allocation (1% HERMES)</span>
                      </div>
                      <span className="font-semibold mono text-sm">{fmt(calc.protocolAllocation)}</span>
                    </div>
                    <div className="flex items-center justify-between bg-amber-500/5 border border-amber-500/10 rounded-lg px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-amber-500" />
                        <span className="text-sm">Treasury (1% BTC/HERMES)</span>
                      </div>
                      <span className="font-semibold mono text-sm">{fmt(calc.treasury)}</span>
                    </div>
                    <div className="flex items-center justify-between bg-violet-500/5 border border-violet-500/10 rounded-lg px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-violet-500" />
                        <span className="text-sm">getbitsy Revenue (1% HERMES)</span>
                      </div>
                      <span className="font-semibold mono text-sm">{fmt(calc.getbitsyRevenue)}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between px-4 py-1">
                      <span className="text-sm font-medium">Total Annual Fee</span>
                      <span className="font-bold mono">{fmt(calc.getbitsyCommission)}</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-1">
                      <span className="text-sm font-medium">On-chain (HERMES)</span>
                      <span className="font-bold mono text-primary">{fmt(calc.hermesOnChain)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-lg px-3 py-2 mt-4">
                    <Award className="w-4 h-4 text-primary shrink-0" />
                    <p className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">{fmt(calc.hermesOnChain)}/yr</span> stays on-chain building the HERMES ecosystem — 50% of your fee circulates back into the protocol community.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Competitor results */}
              {calc.competitors.map(c => {
                const savings = c.totalCost - calc.getbitsyTotal;
                return (
                  <Card key={c.name} data-testid={`result-${c.name.toLowerCase().replace(/\s/g, '-')}`}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-semibold heading-font text-sm">{c.name}</h3>
                          <p className="text-[10px] text-muted-foreground">{c.type}</p>
                        </div>
                        {savings > 0 && (
                          <Badge className="bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20 text-[10px]">
                            {fmt(savings)} more expensive
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Setup</p>
                          <p className="text-lg font-semibold heading-font tabular-nums text-red-500">{fmt(c.setupFeeNum)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{years}yr Cost</p>
                          <p className="text-lg font-semibold heading-font tabular-nums">{fmt(c.totalCost)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Protocol Participation</p>
                          <p className="text-lg font-semibold heading-font tabular-nums text-muted-foreground">$0</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Summary */}
              <Card className="bg-card/60 border-dashed border-2">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-muted-foreground mb-1">With getbitsy, the protocol allocates</p>
                  <p className="text-2xl sm:text-3xl font-semibold heading-font tabular-nums text-primary">
                    {fmt(calc.totalRewardBack)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    worth of HERMES tokens back into the ecosystem over {years} {years === 1 ? 'year' : 'years'} — no other platform does this.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHAT YOU GET ─── */}
      <section className="py-16 sm:py-20 bg-card/30" data-testid="what-you-get-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-lg md:text-xl heading-font font-semibold mb-3">What's included — for every hotel</h2>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto">
              No tiers. No upsells. Every hotel on getbitsy gets the full protocol.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {GETBITSY.features.map((f, i) => (
              <div key={i} className="flex items-center gap-3 bg-background rounded-lg border border-border/60 px-4 py-3" data-testid={`feature-item-${i}`}>
                <div className="w-6 h-6 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <Zap className="w-3 h-3 text-emerald-600" />
                </div>
                <span className="text-sm">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="relative z-10 py-20 sm:py-28 border-t border-[color:var(--l-line)]" data-testid="final-cta-section">
        <div className="absolute inset-0 lobby-glow opacity-70 pointer-events-none" />
        <div className="max-w-3xl mx-auto px-5 text-center relative">
          <div className="text-[13px] tracking-wide text-[color:var(--l-cream-3)] mb-3">Ready when you are</div>
          <h2 className="l-display text-4xl sm:text-5xl lg:text-6xl">
            Your hotel deserves more<br />
            than a <span className="text-[color:var(--l-copper)]">SaaS subscription</span>.
          </h2>
          <p className="text-[color:var(--l-cream-2)] mt-6 max-w-lg mx-auto leading-relaxed">
            Join the protocol. Operate smarter. Keep more of what your rooms earn.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-10">
            <Link to="/register">
              <button className="l-btn-copper h-12 px-7 text-sm inline-flex items-center gap-2" data-testid="final-cta-register">
                <Hotel className="w-4 h-4" /> Get started free
              </button>
            </Link>
            <Link to="/protocol">
              <button className="l-btn-ghost h-12 px-7 text-sm inline-flex items-center gap-2" data-testid="final-cta-protocol">
                <Eye className="w-4 h-4" /> Explore the protocol
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="relative z-10 border-t border-[color:var(--l-line)] py-8">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-6 h-6 rounded-full bg-[color:var(--l-copper)] flex items-center justify-center">
              <span className="l-display text-[color:var(--l-bg)] text-[10px]">b</span>
            </div>
            <span className="l-display text-[14px]">getbitsy</span>
          </Link>
          <div className="flex items-center gap-5 text-[13px] text-[color:var(--l-cream-3)]">
            <Link to="/" className="hover:text-[color:var(--l-cream)] transition-colors">Home</Link>
            <Link to="/marketplace" className="hover:text-[color:var(--l-cream)] transition-colors">Stays</Link>
            <Link to="/protocol" className="hover:text-[color:var(--l-cream)] transition-colors">Protocol</Link>
            <Link to="/bounty-board" className="hover:text-[color:var(--l-cream)] transition-colors">Rewards</Link>
          </div>
          <p className="text-[12px] text-[color:var(--l-cream-3)]">Powered by the HERMES Protocol.</p>
        </div>
      </footer>
    </div>
  );
}
